import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowRight,
  ArrowLeft,
  Building2,
  Users,
  Briefcase,
  DollarSign,
  CreditCard,
  User,
  CheckCircle2,
  Loader2,
  Scan,
  Wallet,
  ExternalLink,
  ShieldCheck,
  Car,
  Home,
  Plane,
  Wrench,
  FileText,
  Fuel,
  Zap,
  Leaf,
  UserCheck,
  MapPin,
  Building,
  Trees,
  RefreshCw,
  Landmark,
  AlertCircle,
  Tag,
  X
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Slider } from "../components/ui/slider";
import PayPalButton from "../components/PayPalButton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Progress } from "../components/ui/progress";
import { useAuth } from "../context/AuthContext";
import IDScanner from "../components/IDScanner";
import SignatureCanvas from "react-signature-canvas";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Loan type options
const LOAN_TYPES = [
  { id: "car", label: "Car", icon: Car, description: "Vehicle financing" },
  { id: "personal", label: "Personal", icon: FileText, description: "Personal loans" },
  { id: "business", label: "Business", icon: Briefcase, description: "Business loans" },
  { id: "equipment", label: "Equipment", icon: Wrench, description: "Equipment financing" },
  { id: "travel", label: "Travel", icon: Plane, description: "Travel financing" },
  { id: "home", label: "Home", icon: Home, description: "Home loans" },
];

// Vehicle type options for car loans
const VEHICLE_TYPES = [
  { id: "gas", label: "Gas", description: "Traditional gasoline vehicle", rate: "6.0%" },
  { id: "electric", label: "Electric (EV)", description: "Fully electric vehicle", rate: "6.75%" },
  { id: "hybrid", label: "Hybrid", description: "Gas-electric hybrid", rate: "6.0%" },
];

// Property type options for mortgage
const PROPERTY_TYPES = [
  { id: "single_family", label: "Single Family", icon: Home, description: "Single family home" },
  { id: "vacant_lot", label: "Vacant Lot", icon: Trees, description: "Land only" },
  { id: "multi_family", label: "Multi-Family", icon: Building, description: "Duplex, triplex, etc." },
  { id: "refinance", label: "Re-Finance", icon: RefreshCw, description: "Refinance existing" },
  { id: "equity_mortgage", label: "Equity Mortgage", icon: Landmark, description: "Home equity loan" },
];

// Form validation schema
const formSchema = z.object({
  // Loan Type
  loan_type: z.enum(["car", "personal", "business", "equipment", "travel", "home"]),
  vehicle_type: z.enum(["gas", "electric", "hybrid"]).optional(),
  cash_injection_ready: z.boolean().optional(),
  
  // Mortgage-specific fields
  property_type: z.enum(["single_family", "vacant_lot", "multi_family", "refinance", "equity_mortgage"]).optional(),
  has_property_in_mind: z.boolean().optional(),
  property_sale_price: z.number().min(0).optional(),
  down_payment_ready: z.boolean().optional(),
  is_first_time_buyer: z.boolean().optional(),
  
  // Life Insurance (for mortgage)
  has_life_insurance: z.boolean().optional(),
  life_insurance_sufficient: z.boolean().optional(),
  life_insurance_premium: z.number().min(0).optional(),
  
  // Co-Applicant (for mortgage)
  has_co_applicant: z.boolean().optional(),
  co_applicant_name: z.string().optional(),
  co_applicant_age: z.number().min(18).max(70).optional(),
  co_applicant_employment_type: z.enum(["government", "private", "self_employed"]).optional(),
  co_applicant_employer_name: z.string().optional(),
  co_applicant_employment_years: z.number().min(0).optional(),
  co_applicant_gross_monthly_salary: z.number().min(0).optional(),
  
  // Closing Cost Add-on
  wants_closing_cost_estimate: z.boolean().optional(),
  
  // Employment
  employment_type: z.enum(["government", "private", "self_employed"]),
  employment_status: z.enum(["full_time", "contract"]).optional(),
  employer_name: z.string().optional(),
  employment_years: z.number().min(0).max(50),
  employment_months: z.number().min(0).max(11),
  contract_end_date: z.string().optional(),
  
  // Citizenship
  citizenship_status: z.enum(["citizen", "permanent_resident", "work_permit", "spousal_permit"]),
  permit_expiry_date: z.string().optional(),
  
  // Personal
  age: z.number().min(18, "Must be at least 18 years old").max(70),
  dependents: z.number().min(0).max(20),
  
  // Income
  gross_monthly_salary: z.number().min(500, "Minimum salary required"),
  other_income: z.number().min(0),
  
  // Debts
  credit_card_payment: z.number().min(0),
  personal_loan_payment: z.number().min(0),
  mortgage_payment: z.number().min(0),
  other_debts: z.number().min(0),
  
  // Loan
  desired_loan_amount: z.number().min(1000, "Minimum loan amount is $1,000"),
  
  // Target monthly payment preference
  target_monthly_payment: z.number().min(0).optional(),
  
  // Loan preference - what matters most
  loan_preference: z.enum(["lower_payment", "lower_rate", "shorter_term", "highest_approval"]).optional(),
  
  // Contact
  full_name: z.string().min(2, "Full name is required"),
  email: z.string().email("Valid email is required"),
  phone: z.string().min(7, "Phone number is required"),

  // CRIF Credit Report fields
  middle_name: z.string().optional(),
  surname: z.string().optional(),
  nib: z.string().optional(),
  date_of_birth: z.string().optional(),
  place_of_birth: z.string().optional(),
  current_address: z.string().optional(),
  address_city: z.string().optional(),
  address_country: z.string().optional(),
  previous_address: z.string().optional(),
  previous_city: z.string().optional(),
  previous_country: z.string().optional(),
  credit_auth_signature: z.string().optional(),
});

const STEPS = [
  { id: 1, title: "Employment\nType", icon: Building2 },
  { id: 2, title: "Loan\nType", icon: CreditCard },
  { id: 3, title: "Employment\nDetails", icon: Briefcase },
  { id: 4, title: "Personal\nInfo", icon: User },
  { id: 5, title: "Income", icon: DollarSign },
  { id: 6, title: "Existing\nDebts", icon: CreditCard },
  { id: 7, title: "Loan\nAmount", icon: DollarSign },
  { id: 8, title: "Credit\nAuth", icon: FileText },
];

export default function QualificationForm() {
  const navigate = useNavigate();
  const { getAuthHeader, user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const [paymentClicked, setPaymentClicked] = useState(false);
  const [transactionId, setTransactionId] = useState("");
  const [isMortgageFlow, setIsMortgageFlow] = useState(false);
  const [closingCosts, setClosingCosts] = useState(null);
  const [calculatingCosts, setCalculatingCosts] = useState(false);
  
  // Promo code state
  const [promoCode, setPromoCode] = useState("");
  const [promoApplied, setPromoApplied] = useState(null);
  const [applyingPromo, setApplyingPromo] = useState(false);
  const [promoError, setPromoError] = useState("");
  
  // Disclaimer popup state
  const [showDisclaimer, setShowDisclaimer] = useState(true);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [sigPadRef, setSigPadRef] = useState(null);
  const [hasSigned, setHasSigned] = useState(false);
  
  // Storage key for form persistence
  const FORM_STORAGE_KEY = "financify_form_state";
  const STEP_STORAGE_KEY = "financify_current_step";
  
  // Load saved form state from localStorage
  const getSavedFormState = () => {
    try {
      const saved = localStorage.getItem(FORM_STORAGE_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error("Error loading saved form state:", e);
    }
    return null;
  };
  
  const getSavedStep = () => {
    try {
      const saved = localStorage.getItem(STEP_STORAGE_KEY);
      if (saved) {
        return parseInt(saved, 10);
      }
    } catch (e) {
      console.error("Error loading saved step:", e);
    }
    return 1;
  };
  
  const savedState = getSavedFormState();
  
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: savedState || {
      loan_type: undefined,
      vehicle_type: undefined,
      cash_injection_ready: undefined,
      property_type: undefined,
      has_property_in_mind: undefined,
      property_sale_price: 0,
      down_payment_ready: undefined,
      is_first_time_buyer: undefined,
      has_life_insurance: undefined,
      life_insurance_sufficient: undefined,
      life_insurance_premium: 0,
      has_co_applicant: undefined,
      co_applicant_name: "",
      co_applicant_age: 30,
      co_applicant_employment_type: undefined,
      co_applicant_employer_name: "",
      co_applicant_employment_years: 0,
      co_applicant_gross_monthly_salary: 0,
      wants_closing_cost_estimate: false,
      loan_preference: undefined,
      employment_type: undefined,
      employment_status: "full_time",
      employer_name: "",
      employment_years: 0,
      employment_months: 0,
      contract_end_date: "",
      citizenship_status: undefined,
      permit_expiry_date: "",
      age: 30,
      dependents: 0,
      gross_monthly_salary: 3000,
      other_income: 0,
      credit_card_payment: 0,
      personal_loan_payment: 0,
      mortgage_payment: 0,
      other_debts: 0,
      desired_loan_amount: 25000,
      target_monthly_payment: 0,
      full_name: "",
      email: "",
      phone: "",
    },
    mode: "onChange"
  });

  const watchLoanType = form.watch("loan_type");
  const watchVehicleType = form.watch("vehicle_type");
  const watchCashInjection = form.watch("cash_injection_ready");
  const watchPropertyType = form.watch("property_type");
  const watchHasPropertyInMind = form.watch("has_property_in_mind");
  const watchPropertySalePrice = form.watch("property_sale_price");
  const watchDownPaymentReady = form.watch("down_payment_ready");
  const watchIsFirstTimeBuyer = form.watch("is_first_time_buyer");
  const watchHasLifeInsurance = form.watch("has_life_insurance");
  const watchLifeInsuranceSufficient = form.watch("life_insurance_sufficient");
  const watchHasCoApplicant = form.watch("has_co_applicant");
  const watchWantsClosingCost = form.watch("wants_closing_cost_estimate");
  const watchEmploymentType = form.watch("employment_type");
  const watchEmploymentStatus = form.watch("employment_status");
  const watchEmploymentYears = form.watch("employment_years");
  const watchCitizenshipStatus = form.watch("citizenship_status");
  const watchPermitExpiry = form.watch("permit_expiry_date");
  const watchSalary = form.watch("gross_monthly_salary") || 0;
  const watchOtherIncome = form.watch("other_income") || 0;
  const watchLoanAmount = form.watch("desired_loan_amount") || 0;
  const watchTargetPayment = form.watch("target_monthly_payment") ?? 0;
  const watchEmployerName = form.watch("employer_name");
  const watchAge = form.watch("age");
  const watchFullName = form.watch("full_name");
  const watchEmail = form.watch("email");
  const watchPhone = form.watch("phone");
  const watchSurname = form.watch("surname");
  const watchNib = form.watch("nib");
  const watchPlaceOfBirth = form.watch("place_of_birth");
  const watchCurrentAddress = form.watch("current_address");
  const watchAddressCity = form.watch("address_city");

  // Restore saved step on mount
  useEffect(() => {
    const savedStep = getSavedStep();
    if (savedStep > 1) {
      setCurrentStep(savedStep);
    }
    
    // Also restore mortgage flow state
    if (savedState?.loan_type === "home") {
      setIsMortgageFlow(true);
    }
  }, []); // Empty dependency array - only run on mount
  
  // Save form state to localStorage on every change
  useEffect(() => {
    const subscription = form.watch((values) => {
      try {
        localStorage.setItem(FORM_STORAGE_KEY, JSON.stringify(values));
      } catch (e) {
        console.error("Error saving form state:", e);
      }
    });
    return () => subscription.unsubscribe();
  }, [form.watch]);
  
  // Save current step to localStorage
  useEffect(() => {
    localStorage.setItem(STEP_STORAGE_KEY, currentStep.toString());
    // Auto-scroll: bring the active step container into view when step changes.
    // We use raw window.scrollTo (not scrollIntoView) with an offset for the
    // sticky header so the step title is clearly visible.
    const scrollToStep = () => {
      const el = document.querySelector(`[data-testid='step-${currentStep}']`)
        || document.querySelector(`[data-step='${currentStep}']`)
        || document.querySelector('[data-testid="qual-active-step"]')
        || document.querySelector('.form-container');
      if (el) {
        const top = el.getBoundingClientRect().top + window.pageYOffset - 100;
        window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    };
    // Fire twice: once after paint, once after animations settle, so the
    // scroll lands reliably even when the step container is tall.
    requestAnimationFrame(() => {
      scrollToStep();
      setTimeout(scrollToStep, 120);
    });
  }, [currentStep]);
  
  // Function to clear saved form state (called after successful submission)
  const clearSavedFormState = () => {
    localStorage.removeItem(FORM_STORAGE_KEY);
    localStorage.removeItem(STEP_STORAGE_KEY);
  };

  // Calculate max loan based on employment type
  const maxLoan = watchLoanType === "home" ? 1000000 : (watchEmploymentType === "government" ? 80000 : 50000);
  const minLoan = watchLoanType === "home" ? 15000 : 1000;
  const loanStep = watchLoanType === "home" ? 5000 : 1000;

  // Calculate TDSR preview
  const totalDebt = 
    (form.watch("credit_card_payment") || 0) +
    (form.watch("personal_loan_payment") || 0) +
    (form.watch("mortgage_payment") || 0) +
    (form.watch("other_debts") || 0);
  const totalIncome = (watchSalary || 0) + (watchOtherIncome || 0);
  const currentTDSR = totalIncome > 0 ? ((totalDebt / totalIncome) * 100).toFixed(1) : 0;

  const progress = (currentStep / STEPS.length) * 100;

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return !!watchEmploymentType;
      case 2:
        // For car loans, require vehicle type and cash injection answer
        if (watchLoanType === "car") {
          return !!watchLoanType && !!watchVehicleType && watchCashInjection !== undefined;
        }
        // For home/mortgage loans, require property type and down payment questions
        if (watchLoanType === "home") {
          if (!watchPropertyType) return false;
          if (watchHasPropertyInMind === undefined) return false;
          if (watchHasPropertyInMind === true) {
            if (!watchPropertySalePrice || watchPropertySalePrice <= 0) return false;
            if (watchDownPaymentReady === undefined) return false;
          }
          return true;
        }
        return !!watchLoanType;
      case 3:
        // Self-employed can proceed even without 2 years (warning shown)
        // But still need a business/employer name
        const employerName = form.watch("employer_name");
        return !!employerName && employerName.length >= 2;
      case 4:
        // Need both age AND citizenship pick (otherwise Zod fails silently on submit)
        if (!(watchAge >= 18)) return false;
        if (!watchCitizenshipStatus) return false;
        // Work-permit / spousal-permit users must also tell us when it expires
        if ((watchCitizenshipStatus === "work_permit" || watchCitizenshipStatus === "spousal_permit") && !watchPermitExpiry) {
          return false;
        }
        return true;
      case 5:
        return watchSalary >= 500;
      case 6:
        return true; // Debts can be 0
      case 7:
        // Skip loan amount for home loans - it's auto-calculated
        if (watchLoanType === "home") {
          return true;
        }
        return watchLoanAmount >= 1000;
      case 8: {
        return !!(watchFullName && watchSurname && watchEmail && watchPhone && watchNib && watchPlaceOfBirth && watchCurrentAddress && watchAddressCity && hasSigned);
      }
      case 9:
        // Always allow proceeding on step 9 if we've reached this step
        // Payment confirmation is checked but we also allow test mode
        return true;
      default:
        return true;
    }
  };

  const handlePayPalClick = () => {
    setPaymentClicked(true);
    // Open PayPal in new tab
    window.open("https://www.paypal.com/ncp/payment/8YYTJMJZSV3FC", "_blank");
  };

  const [verifyingPayment, setVerifyingPayment] = useState(false);
  
  // Apply promo code
  const handleApplyPromo = async () => {
    if (!promoCode.trim()) {
      setPromoError("Please enter a promo code");
      return;
    }
    
    setApplyingPromo(true);
    setPromoError("");
    
    try {
      const response = await axios.post(`${API}/promo/validate`, {
        code: promoCode.trim()
      });
      
      setPromoApplied(response.data);
      
      // If the code gives 100% off, skip payment
      if (response.data.is_free) {
        setPaymentConfirmed(true);
        toast.success("Promo code applied! Payment not required - click 'Get My Results' to continue.");
        
        // Mark promo as used
        await axios.post(`${API}/promo/use`, { code: promoCode.trim() });
      } else {
        toast.success(`Promo code applied! Your new total: $${response.data.final_price.toFixed(2)}`);
      }
    } catch (error) {
      setPromoError(error.response?.data?.detail || "Invalid promo code");
      setPromoApplied(null);
    } finally {
      setApplyingPromo(false);
    }
  };

  const handlePaymentConfirmation = async () => {
    if (!transactionId || transactionId.trim().length < 5) {
      toast.error("Please enter a valid PayPal transaction ID (at least 10 characters)");
      return;
    }
    
    const userEmail = user?.email || form.getValues("email");
    if (!userEmail) {
      toast.error("Please provide your email address first");
      return;
    }
    
    setVerifyingPayment(true);
    
    try {
      // Verify payment with backend
      const response = await axios.post(`${API}/payments/verify`, {
        transaction_id: transactionId.trim(),
        user_email: userEmail
      }, {
        headers: getAuthHeader()
      });
      
      if (response.data.verified) {
        setPaymentConfirmed(true);
        toast.success("Payment verified! Click 'Get My Results' to continue.");
      } else {
        toast.error("Payment verification failed. Please check your transaction ID.");
      }
    } catch (error) {
      console.error("Payment verification error:", error);
      toast.error(error.response?.data?.detail || "Failed to verify payment. Please try again.");
    } finally {
      setVerifyingPayment(false);
    }
  };

  // For mortgage: skip loan amount step entirely
  const nextStep = () => {
    if (currentStep < STEPS.length && canProceed()) {
      let nextStepNum = currentStep + 1;
      // Skip loan amount step (7) for home loans — it's auto-calculated
      // Since step 7 is now the last step, don't skip past it
      if (nextStepNum === 7 && watchLoanType === "home") {
        // For home loans, step 7 still shows but canProceed auto-passes
        // so the submit button will be available
      }
      if (nextStepNum <= STEPS.length) {
        setCurrentStep(nextStepNum);
      }
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      let prevStepNum = currentStep - 1;
      // Skip loan amount step (7) when going back for home loans
      if (prevStepNum === 7 && watchLoanType === "home") {
        prevStepNum = 6; // Go back to Debts step
      }
      setCurrentStep(prevStepNum);
    }
  };

  const onSubmit = async (data) => {
    console.log("Form onSubmit triggered with data:", data);
    
    setIsSubmitting(true);
    toast.info("Processing your application...");
    
    try {
      // Calculate desired loan amount for mortgage
      let calculatedLoanAmount = data.desired_loan_amount;
      
      if (data.loan_type === "home") {
        if (data.has_property_in_mind && data.property_sale_price > 0) {
          // They have a property in mind - loan is 95% of sale price (minus 5% down)
          calculatedLoanAmount = data.property_sale_price * 0.95;
        } else {
          // Just browsing - set to 0, backend will calculate max they can afford
          calculatedLoanAmount = 0;
        }
      }
      
      const payload = {
        loan_type: data.loan_type,
        vehicle_type: data.vehicle_type || null,
        cash_injection_ready: data.cash_injection_ready ?? null,
        // Mortgage fields
        property_type: data.property_type || null,
        has_property_in_mind: data.has_property_in_mind ?? null,
        property_sale_price: data.property_sale_price || null,
        down_payment_ready: data.down_payment_ready ?? null,
        is_first_time_buyer: data.is_first_time_buyer ?? null,
        has_life_insurance: data.has_life_insurance ?? null,
        life_insurance_sufficient: data.life_insurance_sufficient ?? null,
        life_insurance_premium: data.life_insurance_premium || null,
        has_co_applicant: data.has_co_applicant ?? null,
        co_applicant_name: data.co_applicant_name || null,
        co_applicant_age: data.co_applicant_age || null,
        co_applicant_employment_type: data.co_applicant_employment_type || null,
        co_applicant_employer_name: data.co_applicant_employer_name || null,
        co_applicant_employment_years: data.co_applicant_employment_years || null,
        co_applicant_gross_monthly_salary: data.co_applicant_gross_monthly_salary || null,
        wants_closing_cost_estimate: data.wants_closing_cost_estimate ?? false,
        // Employment
        employment_type: data.employment_type,
        employment_status: data.employment_status || null,
        employer_name: data.employer_name || null,
        employment_years: data.employment_years,
        employment_months: data.employment_months,
        contract_end_date: data.contract_end_date || null,
        // Citizenship
        citizenship_status: data.citizenship_status,
        permit_expiry_date: data.permit_expiry_date || null,
        age: data.age,
        dependents: data.dependents,
        gross_monthly_salary: data.gross_monthly_salary,
        other_income: data.other_income,
        existing_debts: {
          credit_cards: data.credit_card_payment > 0 ? [{ monthly_payment: data.credit_card_payment }] : [],
          personal_loans: data.personal_loan_payment > 0 ? [{ monthly_payment: data.personal_loan_payment }] : [],
          mortgage: data.mortgage_payment > 0 ? { monthly_payment: data.mortgage_payment } : null,
          other_debts: data.other_debts,
        },
        desired_loan_amount: calculatedLoanAmount,
        target_monthly_payment: data.target_monthly_payment || 0,
        loan_preference: data.loan_preference || null,
        full_name: user?.full_name || data.full_name,
        email: user?.email || data.email,
        phone: user?.phone || data.phone,
      };

      const response = await axios.post(`${API}/qualify`, payload, {
        headers: getAuthHeader()
      });
      
      // Store result and navigate to free results
      sessionStorage.setItem("qualificationResult", JSON.stringify(response.data));
      
      // Clear saved form state after successful submission
      clearSavedFormState();
      
      toast.success("Application submitted! Loading your results...");
      navigate("/free-results");
      
    } catch (error) {
      console.error("Qualification error:", error);
      const errorMessage = error.response?.data?.detail || error.message || "Something went wrong. Please try again.";
      toast.error(errorMessage);
      alert("Error: " + errorMessage); // Fallback alert for visibility
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'BSD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className="min-h-screen bg-gray-50/50 py-8 md:py-12">
      {/* Disclaimer Popup */}
      {showDisclaimer && !disclaimerAccepted && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="p-6 md:p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-[#52BFB8]/15 flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-[#52BFB8]" />
                </div>
                <h2 className="text-xl font-bold text-[#4A4A4A]">Important Disclaimer</h2>
              </div>
              
              <div className="space-y-4 text-gray-600 text-sm">
                <p>
                  Welcome to Financify! Before you proceed, please understand the following:
                </p>
                
                <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-lg">
                  <p className="text-amber-800 font-medium">
                    Financify is NOT a bank or lender.
                  </p>
                </div>
                
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#52BFB8] mt-0.5 flex-shrink-0" />
                    <span>This is a <strong>pre-qualification estimate only</strong>. Final loan approval is subject to bank review.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#52BFB8] mt-0.5 flex-shrink-0" />
                    <span>We evaluate your information against general bank lending criteria.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#52BFB8] mt-0.5 flex-shrink-0" />
                    <span>Actual loan amounts, rates, and terms may differ.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#52BFB8] mt-0.5 flex-shrink-0" />
                    <span>This does NOT affect your credit score or CRIF report.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#52BFB8] mt-0.5 flex-shrink-0" />
                    <span>Your data is encrypted and never shared without consent.</span>
                  </li>
                </ul>
                
                <p className="text-xs text-gray-500">
                  By continuing, you acknowledge that you have read and understood this disclaimer.
                </p>
              </div>
              
              <div className="mt-6 flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => window.history.back()}
                  className="flex-1"
                >
                  Go Back
                </Button>
                <Button
                  onClick={() => {
                    setDisclaimerAccepted(true);
                    setShowDisclaimer(false);
                  }}
                  className="flex-1 bg-[#52BFB8] hover:bg-[#3DA8A1]"
                  data-testid="accept-disclaimer-btn"
                >
                  I Understand, Continue
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className="container-main">
        {/* Progress Header */}
        <div className="max-w-2xl mx-auto mb-8">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold text-[#4A4A4A]">
              Loan Pre-Qualification
            </h1>
            <div className="flex items-center gap-4">
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm("Are you sure you want to start over? All your progress will be lost.")) {
                      clearSavedFormState();
                      form.reset();
                      setCurrentStep(1);
                      setIsMortgageFlow(false);
                      setPaymentConfirmed(false);
                      setPaymentClicked(false);
                      setTransactionId("");
                    }
                  }}
                  className="text-sm text-gray-500 hover:text-red-600 underline"
                  data-testid="start-over-btn"
                >
                  Start Over
                </button>
              )}
              <span className="text-sm text-gray-500">
                Step {currentStep} of {STEPS.length}
              </span>
            </div>
          </div>
          <Progress value={progress} className="h-2" data-testid="form-progress" />
          
          {/* Step indicator */}
          <div className="flex justify-between mt-4 overflow-x-auto pb-2">
            {STEPS.map((step) => {
              const StepIcon = step.icon;
              const isActive = step.id === currentStep;
              const isCompleted = step.id < currentStep;
              
              return (
                <div 
                  key={step.id} 
                  className={`flex flex-col items-center flex-1 ${
                    isActive ? "text-[#52BFB8]" : isCompleted ? "text-[#52BFB8]" : "text-gray-400"
                  }`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-1 ${
                    isActive ? "bg-[#52BFB8] text-white" : 
                    isCompleted ? "bg-[#52BFB8]/20 text-[#52BFB8]" : 
                    "bg-gray-200"
                  }`}>
                    {isCompleted ? (
                      <CheckCircle2 className="w-4 h-4" />
                    ) : (
                      <StepIcon className="w-4 h-4" />
                    )}
                  </div>
                  <span className="text-[10px] sm:text-xs text-center leading-tight hidden sm:block whitespace-pre-line">{step.title}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Form Container */}
        <div className="form-container">
          <form onSubmit={form.handleSubmit(onSubmit, (errors) => {
            // Spell out which field(s) failed so users don't have to guess.
            // Field → human label + the step on which they live.
            const FIELD_META = {
              loan_type: { label: "Loan type", step: 2 },
              property_type: { label: "Property type", step: 2 },
              property_sale_price: { label: "Property sale price", step: 2 },
              vehicle_type: { label: "Vehicle type", step: 2 },
              employment_type: { label: "Employment type", step: 1 },
              employment_status: { label: "Employment status", step: 3 },
              employer_name: { label: "Employer name", step: 3 },
              employment_years: { label: "Years at current employer", step: 3 },
              employment_months: { label: "Months at current employer", step: 3 },
              citizenship_status: { label: "Citizenship / residency status", step: 4 },
              permit_expiry_date: { label: "Permit expiry date", step: 4 },
              age: { label: "Age", step: 4 },
              dependents: { label: "Number of dependents", step: 4 },
              gross_monthly_salary: { label: "Gross monthly salary", step: 5 },
              other_income: { label: "Other monthly income", step: 5 },
              credit_card_payment: { label: "Credit card payment", step: 6 },
              personal_loan_payment: { label: "Personal loan payment", step: 6 },
              mortgage_payment: { label: "Existing mortgage payment", step: 6 },
              other_debts: { label: "Other monthly debts", step: 6 },
              desired_loan_amount: { label: "Desired loan amount", step: 7 },
              target_monthly_payment: { label: "Target monthly payment", step: 7 },
              loan_preference: { label: "Loan preference", step: 7 },
              full_name: { label: "First name", step: 8 },
              email: { label: "Email", step: 8 },
              phone: { label: "Phone number", step: 8 },
              surname: { label: "Surname", step: 8 },
              nib: { label: "NIB (National Insurance Number)", step: 8 },
              date_of_birth: { label: "Date of birth", step: 8 },
              place_of_birth: { label: "Place of birth", step: 8 },
              current_address: { label: "Current address", step: 8 },
              address_city: { label: "City", step: 8 },
              address_country: { label: "Country", step: 8 },
              credit_auth_signature: { label: "Signature", step: 8 },
            };

            console.error("Form validation errors:", errors);
            const entries = Object.entries(errors || {});
            if (entries.length === 0) {
              toast.error("Please review the form and try again.");
              return;
            }
            // Build a "field — reason" list with step hint
            const items = entries.slice(0, 5).map(([field, err]) => {
              const meta = FIELD_META[field] || { label: field.replace(/_/g, " "), step: null };
              const reason = (err && err.message)
                ? err.message
                : "Required";
              return { field, label: meta.label, step: meta.step, reason };
            });
            const firstStep = items.find((i) => i.step)?.step;
            const extra = entries.length > 5 ? ` …and ${entries.length - 5} more` : "";

            toast.error(
              <div data-testid="form-error-toast">
                <div className="font-semibold mb-1">
                  {items.length === 1 ? "Please fix this field:" : `Please fix ${entries.length} field${entries.length === 1 ? "" : "s"}:`}
                </div>
                <ul className="text-xs leading-5 list-disc list-inside">
                  {items.map((i) => (
                    <li key={i.field}>
                      <strong>{i.label}</strong>
                      {i.step && i.step !== currentStep ? ` (step ${i.step})` : ""}: {i.reason}
                    </li>
                  ))}
                </ul>
                {extra && <div className="text-xs mt-1 text-gray-600">{extra}</div>}
              </div>,
              { duration: 6500 }
            );

            // Jump the user to the earliest step with an error
            if (firstStep && firstStep !== currentStep) {
              setTimeout(() => setCurrentStep(firstStep), 300);
            } else {
              // Focus the first errored input on the current step
              setTimeout(() => {
                const firstName = entries[0][0];
                const el = document.querySelector(`[name='${firstName}']`);
                if (el && typeof el.focus === "function") {
                  el.scrollIntoView({ behavior: "smooth", block: "center" });
                  el.focus({ preventScroll: true });
                }
              }, 200);
            }
          })}>
            {/* Step 1: Employment Type */}
            {currentStep === 1 && (
              <div className="space-y-6" data-testid="step-1">
                <div className="text-center mb-6">
                  <h2 className="text-xl sm:text-2xl font-bold text-[#4A4A4A] mb-2">What type of employer do you work for?</h2>
                  <p className="text-gray-600 text-sm sm:text-base">This helps us match you with the right loan options.</p>
                </div>

                <div className="grid grid-cols-3 gap-2 sm:gap-4">
                  <button
                    type="button"
                    onClick={() => {
                      form.setValue("employment_type", "government");
                      form.setValue("employment_status", "full_time");
                      // Auto-advance to the next step for a frictionless flow
                      setTimeout(() => setCurrentStep(2), 250);
                    }}
                    data-testid="employment-type-government"
                    className={`p-3 sm:p-6 rounded-xl border-2 transition-all ${
                      watchEmploymentType === "government"
                        ? "border-[#52BFB8] bg-[#52BFB8]/5"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <Building2 className={`w-6 h-6 sm:w-10 sm:h-10 mx-auto mb-1 sm:mb-3 ${
                      watchEmploymentType === "government" ? "text-[#52BFB8]" : "text-gray-400"
                    }`} />
                    <p className="font-semibold text-[#4A4A4A] text-xs sm:text-base">Gov't</p>
                    <p className="text-xs text-gray-500 mt-1 hidden sm:block">Public sector</p>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      form.setValue("employment_type", "private");
                      form.setValue("employment_status", "full_time");
                      setTimeout(() => setCurrentStep(2), 250);
                    }}
                    data-testid="employment-type-private"
                    className={`p-3 sm:p-6 rounded-xl border-2 transition-all ${
                      watchEmploymentType === "private"
                        ? "border-[#52BFB8] bg-[#52BFB8]/5"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <Users className={`w-6 h-6 sm:w-10 sm:h-10 mx-auto mb-1 sm:mb-3 ${
                      watchEmploymentType === "private" ? "text-[#52BFB8]" : "text-gray-400"
                    }`} />
                    <p className="font-semibold text-[#4A4A4A] text-xs sm:text-base">Private</p>
                    <p className="text-xs text-gray-500 mt-1 hidden sm:block">Private sector</p>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      form.setValue("employment_type", "self_employed");
                      form.setValue("employment_status", undefined);
                      setTimeout(() => setCurrentStep(2), 250);
                    }}
                    data-testid="employment-type-self-employed"
                    className={`p-3 sm:p-6 rounded-xl border-2 transition-all ${
                      watchEmploymentType === "self_employed"
                        ? "border-[#52BFB8] bg-[#52BFB8]/5"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <UserCheck className={`w-6 h-6 sm:w-10 sm:h-10 mx-auto mb-1 sm:mb-3 ${
                      watchEmploymentType === "self_employed" ? "text-[#52BFB8]" : "text-gray-400"
                    }`} />
                    <p className="font-semibold text-[#4A4A4A] text-xs sm:text-base">Self</p>
                    <p className="text-xs text-gray-500 mt-1 hidden sm:block">Business owner</p>
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Loan Type Selection */}
            {currentStep === 2 && (
              <div className="space-y-6" data-testid="step-2">
                <div className="text-center mb-4">
                  <h2 className="text-xl sm:text-2xl font-bold text-[#4A4A4A] mb-2">What type of loan are you looking for?</h2>
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#52BFB8]/10 rounded-full mt-2">
                    <span className="text-[#52BFB8] text-sm font-medium">No impact to your CRIF score</span>
                  </div>
                </div>

                {/* Loan Type Grid */}
                <div className="bg-gray-50 rounded-2xl p-4 sm:p-6">
                  <div className="grid grid-cols-3 gap-2 sm:gap-4">
                    {LOAN_TYPES.map((loanType) => {
                      const IconComponent = loanType.icon;
                      const isSelected = watchLoanType === loanType.id;
                      
                      return (
                        <button
                          key={loanType.id}
                          type="button"
                          onClick={() => {
                            form.setValue("loan_type", loanType.id);
                            // Reset vehicle type if not car
                            if (loanType.id !== "car") {
                              form.setValue("vehicle_type", undefined);
                              form.setValue("cash_injection_ready", undefined);
                            }
                            // Reset mortgage fields if not home
                            if (loanType.id !== "home") {
                              form.setValue("property_type", undefined);
                              form.setValue("has_property_in_mind", undefined);
                              form.setValue("property_sale_price", 0);
                              form.setValue("down_payment_ready", undefined);
                              setIsMortgageFlow(false);
                            } else {
                              setIsMortgageFlow(true);
                            }
                            // Auto-scroll to the newly revealed sub-section
                            // (mortgage detail block, car detail block, etc.)
                            requestAnimationFrame(() => {
                              setTimeout(() => {
                                const anchor = document.querySelector(
                                  `[data-step2-sub='${loanType.id}']`
                                );
                                if (anchor) {
                                  const top = anchor.getBoundingClientRect().top
                                    + window.pageYOffset - 100;
                                  window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
                                }
                              }, 100);
                            });
                          }}
                          data-testid={`loan-type-${loanType.id}`}
                          className={`p-3 sm:p-5 rounded-xl border-2 transition-all bg-white hover:shadow-md flex flex-col items-center justify-center min-h-[100px] sm:min-h-[120px] ${
                            isSelected
                              ? "border-[#52BFB8] shadow-md"
                              : "border-gray-100 hover:border-[#52BFB8]/50"
                          }`}
                        >
                          <IconComponent 
                            className={`w-6 h-6 sm:w-8 sm:h-8 mb-2 sm:mb-3 ${
                              isSelected ? "text-[#52BFB8]" : "text-[#52BFB8]/70"
                            }`} 
                            strokeWidth={1.5}
                          />
                          <p className={`font-semibold text-xs sm:text-sm text-center leading-tight ${
                            isSelected ? "text-[#4A4A4A]" : "text-gray-700"
                          }`}>
                            {loanType.label}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Mortgage/Home Loan Section */}
                {watchLoanType === "home" && (
                  <div className="bg-purple-50 rounded-2xl p-4 sm:p-6 border border-purple-100 space-y-4" data-step2-sub="home">
                    <h3 className="font-semibold text-[#4A4A4A] mb-4 text-center">
                      What type of property are you interested in?
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                      {PROPERTY_TYPES.map((propType) => {
                        const PropIcon = propType.icon;
                        const isSelected = watchPropertyType === propType.id;
                        return (
                          <button
                            key={propType.id}
                            type="button"
                            onClick={() => form.setValue("property_type", propType.id)}
                            data-testid={`property-type-${propType.id}`}
                            className={`p-3 rounded-xl border-2 transition-all bg-white flex flex-col items-center ${
                              isSelected
                                ? "border-[#52BFB8] shadow-md"
                                : "border-gray-200 hover:border-[#52BFB8]/50"
                            }`}
                          >
                            <PropIcon className={`w-6 h-6 mb-2 ${
                              isSelected ? "text-[#52BFB8]" : "text-gray-400"
                            }`} />
                            <p className="font-semibold text-xs sm:text-sm text-center">{propType.label}</p>
                          </button>
                        );
                      })}
                    </div>

                    {/* Property in Mind Question */}
                    {watchPropertyType && (
                      <div className="mt-4 pt-4 border-t border-purple-200">
                        <p className="text-sm text-[#4A4A4A] mb-3 text-center font-medium">
                          Do you already have a property in mind?
                        </p>
                        <div className="grid grid-cols-2 gap-3">
                          <button
                            type="button"
                            onClick={() => form.setValue("has_property_in_mind", true)}
                            data-testid="has-property-yes"
                            className={`p-3 rounded-xl border-2 transition-all ${
                              watchHasPropertyInMind === true
                                ? "border-[#52BFB8] bg-[#52BFB8]/10"
                                : "border-gray-200 bg-white hover:border-[#52BFB8]/50"
                            }`}
                          >
                            <p className="font-semibold text-sm">Yes</p>
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              form.setValue("has_property_in_mind", false);
                              form.setValue("property_sale_price", 0);
                            }}
                            data-testid="has-property-no"
                            className={`p-3 rounded-xl border-2 transition-all ${
                              watchHasPropertyInMind === false
                                ? "border-[#52BFB8] bg-[#52BFB8]/10"
                                : "border-gray-200 bg-white hover:border-[#52BFB8]/50"
                            }`}
                          >
                            <p className="font-semibold text-sm">No / Still Looking</p>
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Sale Price & Down Payment */}
                    {watchHasPropertyInMind === true && (
                      <div className="mt-4 pt-4 border-t border-purple-200 space-y-4">
                        <div>
                          <Label htmlFor="property_sale_price" className="text-sm font-medium text-[#4A4A4A]">
                            What is the sale price of the property?
                          </Label>
                          <div className="relative mt-1">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                            <Input
                              id="property_sale_price"
                              type="number"
                              placeholder="e.g. 350000"
                              className="pl-8"
                              {...form.register("property_sale_price", { valueAsNumber: true })}
                              data-testid="property-sale-price"
                            />
                          </div>
                        </div>

                        {watchPropertySalePrice > 0 && (
                          <div className="bg-white rounded-xl p-4 border border-purple-200">
                            <div className="flex items-start gap-3">
                              <AlertCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                              <div>
                                <p className="text-sm text-[#4A4A4A] font-medium mb-2">
                                  Minimum down payment required:
                                </p>
                                <p className="text-lg font-bold text-[#52BFB8]">
                                  ${(watchPropertySalePrice * 0.05).toLocaleString()} (5%)
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                  Note: Some banks may require up to 10% (${(watchPropertySalePrice * 0.10).toLocaleString()})
                                </p>
                              </div>
                            </div>
                            
                            <div className="mt-4 pt-4 border-t border-purple-100">
                              <p className="text-sm text-[#4A4A4A] mb-3">
                                Are you prepared for a minimum 5% down payment?
                              </p>
                              <div className="grid grid-cols-2 gap-3">
                                <button
                                  type="button"
                                  onClick={() => form.setValue("down_payment_ready", true)}
                                  data-testid="down-payment-yes"
                                  className={`p-3 rounded-xl border-2 transition-all ${
                                    watchDownPaymentReady === true
                                      ? "border-[#52BFB8] bg-[#52BFB8]/10"
                                      : "border-gray-200 bg-white hover:border-[#52BFB8]/50"
                                  }`}
                                >
                                  <p className="font-semibold text-sm">Yes</p>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => form.setValue("down_payment_ready", false)}
                                  data-testid="down-payment-no"
                                  className={`p-3 rounded-xl border-2 transition-all ${
                                    watchDownPaymentReady === false
                                      ? "border-[#52BFB8] bg-[#52BFB8]/10"
                                      : "border-gray-200 bg-white hover:border-[#52BFB8]/50"
                                  }`}
                                >
                                  <p className="font-semibold text-sm">No</p>
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* First Time Home Buyer */}
                    {watchPropertyType && (
                      <div className="mt-4 pt-4 border-t border-purple-200">
                        <p className="text-sm text-[#4A4A4A] mb-3 font-medium">
                          Are you a first-time home buyer claiming exemption?
                        </p>
                        <div className="grid grid-cols-2 gap-3">
                          <button
                            type="button"
                            onClick={() => form.setValue("is_first_time_buyer", true)}
                            data-testid="first-time-buyer-yes"
                            className={`p-3 rounded-xl border-2 transition-all ${
                              watchIsFirstTimeBuyer === true
                                ? "border-[#52BFB8] bg-[#52BFB8]/10"
                                : "border-gray-200 bg-white hover:border-[#52BFB8]/50"
                            }`}
                          >
                            <p className="font-semibold text-sm">Yes</p>
                          </button>
                          <button
                            type="button"
                            onClick={() => form.setValue("is_first_time_buyer", false)}
                            data-testid="first-time-buyer-no"
                            className={`p-3 rounded-xl border-2 transition-all ${
                              watchIsFirstTimeBuyer === false
                                ? "border-[#52BFB8] bg-[#52BFB8]/10"
                                : "border-gray-200 bg-white hover:border-[#52BFB8]/50"
                            }`}
                          >
                            <p className="font-semibold text-sm">No</p>
                          </button>
                        </div>
                        {watchIsFirstTimeBuyer === true && watchPropertySalePrice > 0 && watchPropertySalePrice < 500000 && (
                          <p className="text-xs text-green-600 mt-2">
                            ✓ You may be exempt from stamp duty (purchase under $500,000)
                          </p>
                        )}
                      </div>
                    )}

                    {/* Life Insurance Questions */}
                    {watchPropertyType && (
                      <div className="mt-4 pt-4 border-t border-purple-200">
                        <p className="text-sm text-[#4A4A4A] mb-3 font-medium">
                          Do you have life insurance coverage already?
                        </p>
                        <div className="grid grid-cols-2 gap-3">
                          <button
                            type="button"
                            onClick={() => form.setValue("has_life_insurance", true)}
                            data-testid="life-insurance-yes"
                            className={`p-3 rounded-xl border-2 transition-all ${
                              watchHasLifeInsurance === true
                                ? "border-[#52BFB8] bg-[#52BFB8]/10"
                                : "border-gray-200 bg-white hover:border-[#52BFB8]/50"
                            }`}
                          >
                            <p className="font-semibold text-sm">Yes</p>
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              form.setValue("has_life_insurance", false);
                              form.setValue("life_insurance_sufficient", undefined);
                              form.setValue("life_insurance_premium", 0);
                            }}
                            data-testid="life-insurance-no"
                            className={`p-3 rounded-xl border-2 transition-all ${
                              watchHasLifeInsurance === false
                                ? "border-[#52BFB8] bg-[#52BFB8]/10"
                                : "border-gray-200 bg-white hover:border-[#52BFB8]/50"
                            }`}
                          >
                            <p className="font-semibold text-sm">No</p>
                          </button>
                        </div>

                        {watchHasLifeInsurance === true && (
                          <div className="mt-3 space-y-3">
                            <div>
                              <p className="text-sm text-[#4A4A4A] mb-2">
                                Is it sufficient to cover the value of the mortgage?
                              </p>
                              <div className="grid grid-cols-2 gap-3">
                                <button
                                  type="button"
                                  onClick={() => form.setValue("life_insurance_sufficient", true)}
                                  className={`p-2 rounded-lg border-2 transition-all text-sm ${
                                    watchLifeInsuranceSufficient === true
                                      ? "border-[#52BFB8] bg-[#52BFB8]/10"
                                      : "border-gray-200 bg-white"
                                  }`}
                                >
                                  Yes
                                </button>
                                <button
                                  type="button"
                                  onClick={() => form.setValue("life_insurance_sufficient", false)}
                                  className={`p-2 rounded-lg border-2 transition-all text-sm ${
                                    watchLifeInsuranceSufficient === false
                                      ? "border-[#52BFB8] bg-[#52BFB8]/10"
                                      : "border-gray-200 bg-white"
                                  }`}
                                >
                                  No
                                </button>
                              </div>
                            </div>

                            {watchLifeInsuranceSufficient === true && (
                              <div>
                                <Label className="text-sm">Yearly premium amount ($)</Label>
                                <Input
                                  type="number"
                                  placeholder="e.g. 1200"
                                  className="mt-1"
                                  {...form.register("life_insurance_premium", { valueAsNumber: true })}
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Co-Applicant Question */}
                    {watchPropertyType && (
                      <div className="mt-4 pt-4 border-t border-purple-200">
                        <p className="text-sm text-[#4A4A4A] mb-3 font-medium">
                          Will there be a co-applicant on this mortgage application?
                        </p>
                        <div className="grid grid-cols-2 gap-3">
                          <button
                            type="button"
                            onClick={() => form.setValue("has_co_applicant", true)}
                            data-testid="co-applicant-yes"
                            className={`p-3 rounded-xl border-2 transition-all ${
                              watchHasCoApplicant === true
                                ? "border-[#52BFB8] bg-[#52BFB8]/10"
                                : "border-gray-200 bg-white hover:border-[#52BFB8]/50"
                            }`}
                          >
                            <p className="font-semibold text-sm">Yes</p>
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              form.setValue("has_co_applicant", false);
                              form.setValue("co_applicant_name", "");
                            }}
                            data-testid="co-applicant-no"
                            className={`p-3 rounded-xl border-2 transition-all ${
                              watchHasCoApplicant === false
                                ? "border-[#52BFB8] bg-[#52BFB8]/10"
                                : "border-gray-200 bg-white hover:border-[#52BFB8]/50"
                            }`}
                          >
                            <p className="font-semibold text-sm">No</p>
                          </button>
                        </div>
                        {watchHasCoApplicant === true && (
                          <p className="text-xs text-blue-600 mt-2">
                            ℹ️ Co-applicant details will be collected after your information
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Vehicle Type Selection - Only shows when Car is selected */}
                {watchLoanType === "car" && (
                  <div className="bg-blue-50 rounded-2xl p-4 sm:p-6 border border-blue-100" data-step2-sub="car">
                    <h3 className="font-semibold text-[#4A4A4A] mb-4 text-center">
                      What type of vehicle are you looking to finance?
                    </h3>
                    <div className="grid grid-cols-3 gap-2 sm:gap-4">
                      {/* Gas */}
                      <button
                        type="button"
                        onClick={() => form.setValue("vehicle_type", "gas")}
                        data-testid="vehicle-type-gas"
                        className={`p-3 sm:p-4 rounded-xl border-2 transition-all bg-white flex flex-col items-center ${
                          watchVehicleType === "gas"
                            ? "border-[#52BFB8] shadow-md"
                            : "border-gray-200 hover:border-[#52BFB8]/50"
                        }`}
                      >
                        <Fuel className={`w-6 h-6 sm:w-8 sm:h-8 mb-2 ${
                          watchVehicleType === "gas" ? "text-[#52BFB8]" : "text-gray-400"
                        }`} />
                        <p className="font-semibold text-xs sm:text-sm">Gas</p>
                      </button>

                      {/* Electric */}
                      <button
                        type="button"
                        onClick={() => form.setValue("vehicle_type", "electric")}
                        data-testid="vehicle-type-electric"
                        className={`p-3 sm:p-4 rounded-xl border-2 transition-all bg-white flex flex-col items-center ${
                          watchVehicleType === "electric"
                            ? "border-[#52BFB8] shadow-md"
                            : "border-gray-200 hover:border-[#52BFB8]/50"
                        }`}
                      >
                        <Zap className={`w-6 h-6 sm:w-8 sm:h-8 mb-2 ${
                          watchVehicleType === "electric" ? "text-[#52BFB8]" : "text-gray-400"
                        }`} />
                        <p className="font-semibold text-xs sm:text-sm">Electric</p>
                      </button>

                      {/* Hybrid */}
                      <button
                        type="button"
                        onClick={() => form.setValue("vehicle_type", "hybrid")}
                        data-testid="vehicle-type-hybrid"
                        className={`p-3 sm:p-4 rounded-xl border-2 transition-all bg-white flex flex-col items-center ${
                          watchVehicleType === "hybrid"
                            ? "border-[#52BFB8] shadow-md"
                            : "border-gray-200 hover:border-[#52BFB8]/50"
                        }`}
                      >
                        <Leaf className={`w-6 h-6 sm:w-8 sm:h-8 mb-2 ${
                          watchVehicleType === "hybrid" ? "text-[#52BFB8]" : "text-gray-400"
                        }`} />
                        <p className="font-semibold text-xs sm:text-sm">Hybrid</p>
                      </button>
                    </div>

                    {/* Cash Injection Question */}
                    {watchVehicleType && (
                      <div className="mt-4 pt-4 border-t border-blue-200">
                        <p className="text-sm text-[#4A4A4A] mb-3 text-center">
                          Can you provide a 15% cash injection if needed?
                        </p>
                        <div className="grid grid-cols-2 gap-3">
                          <button
                            type="button"
                            onClick={() => form.setValue("cash_injection_ready", true)}
                            data-testid="cash-injection-yes"
                            className={`p-3 rounded-xl border-2 transition-all ${
                              watchCashInjection === true
                                ? "border-[#52BFB8] bg-[#52BFB8]/10"
                                : "border-gray-200 bg-white hover:border-[#52BFB8]/50"
                            }`}
                          >
                            <p className="font-semibold text-sm">Yes</p>
                          </button>
                          <button
                            type="button"
                            onClick={() => form.setValue("cash_injection_ready", false)}
                            data-testid="cash-injection-no"
                            className={`p-3 rounded-xl border-2 transition-all ${
                              watchCashInjection === false
                                ? "border-[#52BFB8] bg-[#52BFB8]/10"
                                : "border-gray-200 bg-white hover:border-[#52BFB8]/50"
                            }`}
                          >
                            <p className="font-semibold text-sm">No</p>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Trust Badge */}
                <div className="flex items-center justify-center gap-2 text-gray-500 text-xs sm:text-sm">
                  <ShieldCheck className="w-4 h-4 flex-shrink-0" />
                  <span>No data shared. No biased deals. <strong className="text-gray-700">Just Results.</strong></span>
                </div>
              </div>
            )}

            {/* Step 3: Employment Details */}
            {currentStep === 3 && (
              <div className="space-y-6" data-testid="step-3">
                <div className="text-center mb-4">
                  <h2 className="text-xl sm:text-2xl font-bold text-[#4A4A4A] mb-2">
                    {watchEmploymentType === "self_employed" 
                      ? "Tell us about your business" 
                      : "Tell us about your employment"}
                  </h2>
                  <p className="text-gray-600 text-sm sm:text-base">
                    {watchEmploymentType === "self_employed"
                      ? "We need a few details about your self-employment."
                      : "We need a few details about your employment. No data will be shared."}
                  </p>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label htmlFor="employer_name">
                      {watchEmploymentType === "self_employed" ? "Business Name" : "Employer Name"}
                    </Label>
                    <Input
                      id="employer_name"
                      placeholder={watchEmploymentType === "self_employed" 
                        ? "e.g. Smith's Auto Repair" 
                        : "e.g. Ministry of Finance"}
                      {...form.register("employer_name")}
                      data-testid="input-employer-name"
                      className="input-base mt-1"
                    />
                  </div>

                  {/* Only show Employment Status for non-self-employed */}
                  {watchEmploymentType !== "self_employed" && (
                    <div>
                      <Label>Employment Status</Label>
                      <Select
                        value={watchEmploymentStatus}
                        onValueChange={(value) => form.setValue("employment_status", value)}
                      >
                        <SelectTrigger className="input-base mt-1" data-testid="select-employment-status">
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="full_time">Full-Time Permanent</SelectItem>
                          <SelectItem value="contract">Contract Worker</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="employment_years">
                        {watchEmploymentType === "self_employed" ? "Years in Business" : "Years at Job"}
                      </Label>
                      <Input
                        id="employment_years"
                        type="number"
                        min="0"
                        max="50"
                        {...form.register("employment_years", { valueAsNumber: true })}
                        data-testid="input-employment-years"
                        className="input-base mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="employment_months">Months</Label>
                      <Input
                        id="employment_months"
                        type="number"
                        min="0"
                        max="11"
                        {...form.register("employment_months", { valueAsNumber: true })}
                        data-testid="input-employment-months"
                        className="input-base mt-1"
                      />
                    </div>
                  </div>

                  {/* Self-employed warning if less than 2 years */}
                  {watchEmploymentType === "self_employed" && watchEmploymentYears < 2 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm text-amber-800 font-medium">
                            Note: Most banks require a minimum of 2 years of self-employment.
                          </p>
                          <p className="text-xs text-amber-600 mt-1">
                            You can still continue, and this will be included in your assessment.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {watchEmploymentStatus === "contract" && watchEmploymentType !== "self_employed" && (
                    <div>
                      <Label htmlFor="contract_end_date">Contract End Date</Label>
                      <Input
                        id="contract_end_date"
                        type="date"
                        {...form.register("contract_end_date")}
                        data-testid="input-contract-end-date"
                        className="input-base mt-1"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 4: Personal Info */}
            {currentStep === 4 && (
              <div className="space-y-6" data-testid="step-4">
                <div className="text-center mb-4">
                  <h2 className="text-xl sm:text-2xl font-bold text-[#4A4A4A] mb-2">A bit about you</h2>
                  <p className="text-gray-600 text-sm sm:text-base">This helps determine your loan eligibility.</p>
                </div>

                {/* Optional ID Scanner — autofills fields below */}
                <div className="bg-gradient-to-br from-[#52BFB8]/5 to-transparent border border-[#52BFB8]/20 rounded-2xl p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="text-sm font-semibold text-[#4A4A4A]">Quick Verify with ID Scan</h3>
                      <p className="text-xs text-gray-500">Snap your NIB Card, Passport, or Driver's License — we'll auto-fill your details.</p>
                    </div>
                  </div>
                  <IDScanner
                    authHeader={getAuthHeader()}
                    onDataExtracted={(data) => {
                      if (data.full_name) {
                        const parts = data.full_name.trim().split(/\s+/);
                        form.setValue("full_name", parts[0] || "");
                        if (parts.length > 2) {
                          form.setValue("middle_name", parts.slice(1, -1).join(" "));
                          form.setValue("surname", parts[parts.length - 1]);
                        } else if (parts.length === 2) {
                          form.setValue("surname", parts[1]);
                        }
                      }
                      if (data.date_of_birth) form.setValue("date_of_birth", data.date_of_birth);
                      if (data.age && !form.getValues("age")) form.setValue("age", data.age);
                      if (data.id_number && !form.getValues("nib")) form.setValue("nib", data.id_number);
                      if (data.address && !form.getValues("current_address")) form.setValue("current_address", data.address);
                    }}
                  />
                </div>

                <div className="space-y-6">
                  {/* Citizenship Status */}
                  <div>
                    <Label className="text-base font-medium mb-3 block">Citizenship Status</Label>
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { id: "citizen", label: "Citizen", icon: "🇧🇸" },
                        { id: "permanent_resident", label: "Permanent Resident", icon: "🏠" },
                        { id: "work_permit", label: "Work Permit Holder", icon: "💼" },
                        { id: "spousal_permit", label: "Spousal Permit Holder", icon: "💑" },
                      ].map((status) => (
                        <button
                          key={status.id}
                          type="button"
                          onClick={() => {
                            form.setValue("citizenship_status", status.id);
                            // Clear permit expiry if switching to citizen/PR
                            if (status.id === "citizen" || status.id === "permanent_resident") {
                              form.setValue("permit_expiry_date", "");
                            }
                          }}
                          className={`p-4 rounded-xl border-2 transition-all text-left ${
                            watchCitizenshipStatus === status.id
                              ? "border-[#52BFB8] bg-[#52BFB8]/5"
                              : "border-gray-200 hover:border-gray-300"
                          }`}
                          data-testid={`citizenship-${status.id}`}
                        >
                          <span className="text-2xl mb-2 block">{status.icon}</span>
                          <span className="font-medium text-[#4A4A4A] text-sm">{status.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Permit Expiry Date - Only show for permit holders */}
                  {(watchCitizenshipStatus === "work_permit" || watchCitizenshipStatus === "spousal_permit") && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                      <Label className="text-base font-medium mb-2 block text-amber-800">
                        When does your permit expire?
                      </Label>
                      <p className="text-sm text-amber-600 mb-3">
                        Your maximum loan term will be limited to your permit validity period.
                      </p>
                      <Input
                        type="date"
                        value={watchPermitExpiry || ""}
                        onChange={(e) => form.setValue("permit_expiry_date", e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        max={new Date(Date.now() + 15 * 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                        className="bg-white"
                        data-testid="permit-expiry-date"
                      />
                      {watchPermitExpiry && (
                        <p className="text-sm text-amber-700 mt-2">
                          Maximum loan term available: {(() => {
                            const expiry = new Date(watchPermitExpiry);
                            const now = new Date();
                            const years = Math.floor((expiry - now) / (365 * 24 * 60 * 60 * 1000));
                            const months = Math.floor(((expiry - now) % (365 * 24 * 60 * 60 * 1000)) / (30 * 24 * 60 * 60 * 1000));
                            if (years <= 0 && months <= 0) return "Permit expired";
                            return `${years > 0 ? `${years} year${years > 1 ? 's' : ''}` : ''} ${months > 0 ? `${months} month${months > 1 ? 's' : ''}` : ''}`.trim();
                          })()}
                        </p>
                      )}
                    </div>
                  )}

                  <div>
                    <Label>Your Age: {form.watch("age")} years</Label>
                    <Slider
                      value={[form.watch("age")]}
                      onValueChange={([value]) => form.setValue("age", value)}
                      min={18}
                      max={70}
                      step={1}
                      className="mt-4"
                      data-testid="slider-age"
                    />
                    <div className="flex justify-between text-sm text-gray-400 mt-1">
                      <span>18</span>
                      <span>70</span>
                    </div>
                  </div>

                  <div>
                    <Label>Number of Dependents: {form.watch("dependents")}</Label>
                    <Slider
                      value={[form.watch("dependents")]}
                      onValueChange={([value]) => form.setValue("dependents", value)}
                      min={0}
                      max={10}
                      step={1}
                      className="mt-4"
                      data-testid="slider-dependents"
                    />
                    <div className="flex justify-between text-sm text-gray-400 mt-1">
                      <span>0</span>
                      <span>10+</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 5: Income */}
            {currentStep === 5 && (
              <div className="space-y-6" data-testid="step-5">
                <div className="text-center mb-8">
                  <h2 className="heading-3 mb-2">What's your monthly income?</h2>
                  <p className="text-gray-600">Include your gross salary before N.I.B deductions.</p>
                </div>

                <div className="space-y-6">
                  <div>
                    <Label>Gross Monthly Salary: {formatCurrency(watchSalary)}</Label>
                    <Slider
                      value={[watchSalary]}
                      onValueChange={([value]) => form.setValue("gross_monthly_salary", value)}
                      min={500}
                      max={20000}
                      step={100}
                      className="mt-4"
                      data-testid="slider-salary"
                    />
                    <div className="flex justify-between text-sm text-gray-400 mt-1">
                      <span>$500</span>
                      <span>$20,000+</span>
                    </div>
                  </div>

                  <div>
                    <Label>Other Monthly Income (optional): {formatCurrency(watchOtherIncome)}</Label>
                    <Slider
                      value={[watchOtherIncome]}
                      onValueChange={([value]) => form.setValue("other_income", value)}
                      min={0}
                      max={10000}
                      step={100}
                      className="mt-4"
                      data-testid="slider-other-income"
                    />
                    <div className="flex justify-between text-sm text-gray-400 mt-1">
                      <span>$0</span>
                      <span>$10,000+</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      e.g., rental income, side business, investments
                    </p>
                  </div>

                  <div className="bg-[#52BFB8]/10 rounded-xl p-4">
                    <p className="text-sm text-[#4A4A4A]">
                      <strong>Total Monthly Income:</strong> {formatCurrency(totalIncome)}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Step 6: Existing Debts */}
            {currentStep === 6 && (
              <div className="space-y-6" data-testid="step-6">
                <div className="text-center mb-8">
                  <h2 className="heading-3 mb-2">What are your monthly debt payments?</h2>
                  <p className="text-gray-600">This helps calculate your debt-to-income ratio.</p>
                </div>

                <div className="space-y-5">
                  <div>
                    <Label htmlFor="credit_card_payment">Credit Card Payments</Label>
                    <div className="relative mt-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                      <Input
                        id="credit_card_payment"
                        type="number"
                        min="0"
                        placeholder="0"
                        {...form.register("credit_card_payment", { valueAsNumber: true })}
                        data-testid="input-credit-card"
                        className="input-base pl-8"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="personal_loan_payment">Personal Loan Payments</Label>
                    <div className="relative mt-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                      <Input
                        id="personal_loan_payment"
                        type="number"
                        min="0"
                        placeholder="0"
                        {...form.register("personal_loan_payment", { valueAsNumber: true })}
                        data-testid="input-personal-loan"
                        className="input-base pl-8"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="mortgage_payment">Mortgage Payments</Label>
                    <div className="relative mt-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                      <Input
                        id="mortgage_payment"
                        type="number"
                        min="0"
                        placeholder="0"
                        {...form.register("mortgage_payment", { valueAsNumber: true })}
                        data-testid="input-mortgage"
                        className="input-base pl-8"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="other_debts">Other Monthly Debts</Label>
                    <div className="relative mt-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                      <Input
                        id="other_debts"
                        type="number"
                        min="0"
                        placeholder="0"
                        {...form.register("other_debts", { valueAsNumber: true })}
                        data-testid="input-other-debts"
                        className="input-base pl-8"
                      />
                    </div>
                  </div>

                  <div className={`rounded-xl p-4 ${
                    parseFloat(currentTDSR) > 40 ? "bg-red-50" : "bg-[#52BFB8]/10"
                  }`}>
                    <p className="text-sm text-[#4A4A4A]">
                      <strong>Current TDSR:</strong> {currentTDSR}%
                      {parseFloat(currentTDSR) > 40 && (
                        <span className="block text-red-600 text-xs mt-1">
                          Note: High debt ratio may affect eligibility (max 45%)
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Step 7: Loan Amount */}
            {currentStep === 7 && (
              <div className="space-y-6" data-testid="step-7">
                <div className="text-center mb-8">
                  <h2 className="heading-3 mb-2">How much would you like to pre-qualify for?</h2>
                  <p className="text-gray-600">
                    {watchLoanType === "home" 
                      ? "Mortgage pre-qualification from $15,000 up to $1,000,000."
                      : "Some banks offer up to $100,000 in consumer loans."}
                  </p>
                </div>

                <div className="space-y-6">
                  <div className="text-center">
                    <span className="text-5xl font-bold text-[#4A4A4A]">
                      {formatCurrency(watchLoanAmount)}
                    </span>
                  </div>

                  <Slider
                    value={[watchLoanAmount]}
                    onValueChange={([value]) => form.setValue("desired_loan_amount", value)}
                    min={minLoan}
                    max={maxLoan}
                    step={loanStep}
                    className="mt-4"
                    data-testid="slider-loan-amount"
                  />
                  <div className="flex justify-between text-sm text-gray-400">
                    <span>{formatCurrency(minLoan)}</span>
                    <span>{formatCurrency(maxLoan)}</span>
                  </div>

                  {/* Custom amount input */}
                  <div className="flex items-center gap-3 mt-4">
                    <Label className="text-sm text-gray-500 whitespace-nowrap">Or enter amount:</Label>
                    <Input
                      type="number"
                      min={minLoan}
                      max={maxLoan}
                      value={watchLoanAmount || ""}
                      onChange={(e) => {
                        const val = parseInt(e.target.value) || 0;
                        if (val >= 0 && val <= maxLoan) form.setValue("desired_loan_amount", val);
                      }}
                      className="w-40"
                      placeholder="$0"
                      data-testid="input-custom-amount"
                    />
                  </div>

                  {/* Quick select buttons */}
                  <div className="grid grid-cols-3 gap-2 mt-4">
                    {(watchLoanType === "home" 
                      ? [100000, 250000, 500000]
                      : [10000, 25000, maxLoan === 80000 ? 50000 : 30000]
                    ).map((amount) => (
                      <Button
                        key={amount}
                        type="button"
                        variant="outline"
                        onClick={() => form.setValue("desired_loan_amount", amount)}
                        className={watchLoanAmount === amount ? "border-[#52BFB8] bg-[#52BFB8]/5" : ""}
                        data-testid={`quick-select-${amount}`}
                      >
                        {formatCurrency(amount)}
                      </Button>
                    ))}
                  </div>

                  {/* Target Monthly Payment Section */}
                  <div className="mt-8 pt-6 border-t border-gray-200">
                    <h3 className="text-lg font-semibold text-[#4A4A4A] mb-2">What monthly payment works for you?</h3>
                    <p className="text-sm text-gray-500 mb-6">We'll find options that match your budget. Leave at $0 to see all options.</p>
                    
                    <div className="text-center mb-4">
                      {(!watchTargetPayment || watchTargetPayment === 0) ? (
                        <span className="text-2xl md:text-3xl font-bold text-[#52BFB8]">No preference</span>
                      ) : (
                        <>
                          <span className="text-3xl md:text-4xl font-bold text-[#52BFB8]">
                            {formatCurrency(watchTargetPayment)}
                          </span>
                          <span className="text-gray-500 text-lg">/month</span>
                        </>
                      )}
                    </div>

                    <Slider
                      value={[watchTargetPayment || 0]}
                      onValueChange={([value]) => form.setValue("target_monthly_payment", value)}
                      min={0}
                      max={3000}
                      step={50}
                      className="mt-4"
                      data-testid="slider-target-payment"
                    />
                    <div className="flex justify-between text-sm text-gray-400 mt-2">
                      <span>No preference</span>
                      <span>$3,000/mo</span>
                    </div>

                    {/* Quick select payment amounts */}
                    <div className="grid grid-cols-4 gap-2 mt-4">
                      {[0, 500, 1000, 1500].map((amount) => (
                        <Button
                          key={amount}
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => form.setValue("target_monthly_payment", amount)}
                          className={(watchTargetPayment || 0) === amount ? "border-[#52BFB8] bg-[#52BFB8]/5 text-[#52BFB8]" : "text-gray-600"}
                          data-testid={`quick-payment-${amount}`}
                        >
                          {amount === 0 ? "Any" : `BSD ${amount.toLocaleString()}`}
                        </Button>
                      ))}
                    </div>
                  </div>
                  
                  {/* What matters most preference */}
                  <div className="mt-8 pt-6 border-t border-gray-200">
                    <h3 className="text-lg font-semibold text-[#4A4A4A] mb-4">What matters most to you?</h3>
                    <p className="text-sm text-gray-500 mb-4">This helps us personalize your loan recommendations.</p>
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { id: "lower_payment", label: "Lower Monthly Payment", icon: DollarSign },
                        { id: "lower_rate", label: "Lower Interest Rate", icon: CreditCard },
                        { id: "shorter_term", label: "Shorter Loan Term", icon: Briefcase },
                        { id: "highest_approval", label: "Highest Approval Chance", icon: CheckCircle2 },
                      ].map((pref) => {
                        const PrefIcon = pref.icon;
                        const isSelected = form.watch("loan_preference") === pref.id;
                        return (
                          <button
                            key={pref.id}
                            type="button"
                            onClick={() => form.setValue("loan_preference", pref.id)}
                            data-testid={`preference-${pref.id}`}
                            className={`p-4 rounded-xl border-2 text-left transition-all ${
                              isSelected
                                ? "border-[#52BFB8] bg-[#52BFB8]/5"
                                : "border-gray-200 hover:border-gray-300"
                            }`}
                          >
                            <PrefIcon className={`w-5 h-5 mb-2 ${isSelected ? "text-[#52BFB8]" : "text-gray-400"}`} />
                            <span className={`text-sm font-medium ${isSelected ? "text-[#52BFB8]" : "text-gray-700"}`}>
                              {pref.label}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}


            {/* Step 8: Credit Authorization */}
            {currentStep === 8 && (
              <div className="space-y-5" data-testid="step-8">
                <div className="text-center mb-4">
                  <h2 className="text-xl sm:text-2xl font-bold text-[#4A4A4A] mb-2">Authorize Your Credit Check</h2>
                  <p className="text-gray-600 text-sm">Complete the fields below and sign to authorize CRIF Bahamas to release your credit report.</p>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs">First Name *</Label>
                      <Input {...form.register("full_name")} placeholder="First name" className="mt-1" data-testid="input-first-name" />
                    </div>
                    <div>
                      <Label className="text-xs">Middle Name</Label>
                      <Input {...form.register("middle_name")} placeholder="Optional" className="mt-1" data-testid="input-middle-name" />
                    </div>
                    <div>
                      <Label className="text-xs">Surname *</Label>
                      <Input {...form.register("surname")} placeholder="Surname" className="mt-1" data-testid="input-surname" />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Email *</Label>
                      <Input {...form.register("email")} type="email" placeholder="john@email.com" className="mt-1" data-testid="input-email-crif" />
                    </div>
                    <div>
                      <Label className="text-xs">Phone *</Label>
                      <Input {...form.register("phone")} placeholder="(242) 000-0000" className="mt-1" data-testid="input-phone-crif" />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">National Insurance Number (NIB) *</Label>
                      <Input {...form.register("nib")} placeholder="Enter NIB number" className="mt-1" data-testid="input-nib" />
                    </div>
                    <div>
                      <Label className="text-xs">Date of Birth</Label>
                      <Input type="date" {...form.register("date_of_birth")} className="mt-1" data-testid="input-dob" />
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs">Place of Birth (City, Country) *</Label>
                    <Input {...form.register("place_of_birth")} placeholder="e.g. Nassau, Bahamas" className="mt-1" data-testid="input-pob" />
                  </div>

                  <div>
                    <Label className="text-xs">Current Address *</Label>
                    <Input {...form.register("current_address")} placeholder="Street address" className="mt-1" data-testid="input-address" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">City *</Label>
                      <Input {...form.register("address_city")} placeholder="e.g. Nassau" className="mt-1" data-testid="input-city" />
                    </div>
                    <div>
                      <Label className="text-xs">Country</Label>
                      <Input {...form.register("address_country")} placeholder="Bahamas" className="mt-1" data-testid="input-country" />
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <p className="text-xs font-medium text-gray-500 mb-2">Previous Address (Optional)</p>
                    <Input {...form.register("previous_address")} placeholder="Street address" className="mb-2" data-testid="input-prev-address" />
                    <div className="grid grid-cols-2 gap-2">
                      <Input {...form.register("previous_city")} placeholder="City" data-testid="input-prev-city" />
                      <Input {...form.register("previous_country")} placeholder="Country" data-testid="input-prev-country" />
                    </div>
                  </div>
                </div>

                <div className="bg-[#0a1628] rounded-xl p-5 mt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <ShieldCheck className="w-5 h-5 text-[#52BFB8]" />
                    <h3 className="text-sm font-semibold text-white">CRIF Authorization</h3>
                  </div>
                  <p className="text-xs text-slate-300 leading-relaxed mb-4">
                    I hereby grant CRIF Information Services Bahamas Limited the authorization to release to me a copy of my Credit Report. I am aware that I have the legal right to challenge incorrect, incomplete or unreliable information should this arise.
                  </p>

                  <div>
                    <Label className="text-xs text-slate-400 mb-2 block">Your Signature *</Label>
                    <div className="bg-white rounded-lg border-2 border-dashed border-gray-300 relative" style={{ height: 120 }}>
                      <SignatureCanvas
                        ref={(ref) => setSigPadRef(ref)}
                        canvasProps={{ className: "w-full h-full rounded-lg", "data-testid": "signature-canvas" }}
                        onEnd={() => {
                          if (sigPadRef && !sigPadRef.isEmpty()) {
                            const dataUrl = sigPadRef.getCanvas().toDataURL("image/png");
                            form.setValue("credit_auth_signature", dataUrl);
                            setHasSigned(true);
                          }
                        }}
                      />
                      {!hasSigned && (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <span className="text-sm text-gray-300">Sign here</span>
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        if (sigPadRef) {
                          sigPadRef.clear();
                          form.setValue("credit_auth_signature", "");
                          setHasSigned(false);
                        }
                      }}
                      className="text-xs text-[#52BFB8] hover:underline mt-1"
                      data-testid="clear-signature"
                    >
                      Clear signature
                    </button>
                  </div>
                </div>
              </div>
            )}


            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={prevStep}
                disabled={currentStep === 1}
                className="btn-secondary"
                data-testid="btn-prev"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>

              {currentStep < STEPS.length ? (
                <Button
                  type="button"
                  onClick={nextStep}
                  disabled={!canProceed()}
                  className="btn-primary"
                  data-testid="btn-next"
                >
                  Continue
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  type="submit"
                  disabled={!canProceed() || isSubmitting}
                  className="btn-primary"
                  data-testid="btn-submit"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Get My Results
                    </>
                  )}
                </Button>
              )}
            </div>
          </form>
        </div>

        {/* Disclaimer */}
        <p className="text-center text-xs text-gray-400 mt-6 max-w-lg mx-auto">
          This pre-qualification check does not affect your CRIF report. 
          Final approval subject to bank verification/KYC, and any additional information they may require.
        </p>
      </div>
    </div>
  );
}
